// src/components/Clients.js
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";

import {
  FaSearch,
  FaTrashAlt,
  FaFilter,
  FaGlobeEurope,
} from "react-icons/fa";

import {
  LuFactory,
  LuTruck,
  LuPhone,
  LuMapPin,
  LuBuilding2,
  LuHardHat,
  LuCpu,
  LuGraduationCap,
  LuCar,
  LuWheat,
  LuZap,
  LuLandmark,
  LuUtensilsCrossed,
  LuHeartPulse,
  LuHotel,
  LuClapperboard,
  LuPickaxe,
  LuPill,
  LuHouse,
  LuShoppingBag,
  LuDroplets,
  LuEllipsis,
} from "react-icons/lu";

import Select from "react-select";

import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import axios from "../axiosConfig";

import {
  getSocket,
} from "../lib/socket";

import {
  getStoredToken,
  getStoredUser,
} from "../lib/authStorage";

import {
  writeIndustryCache,
  readIndustryCache,
} from "../lib/industryEditCache";

import {
  writeCarrierCache,
  readCarrierCache,
} from "../lib/carrierEditCache";

import "../styles/Clients.css";

import {
  useTranslation,
} from "react-i18next";

/* =========================================================
   Helpers
   ========================================================= */

const PAGE_SIZE = 20;

const CLIENTS_CACHE_TTL_MS =
  5 * 60 * 1000;

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const stringifyValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .join(", ");
  }

  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
};

async function prefetchIndustryFull(id) {
  try {
    const {
      data,
    } = await axios.get(
      `/industries/${id}`
    );

    if (data) {
      writeIndustryCache(
        id,
        data
      );
    }
  } catch {
    // ignore
  }
}

async function prefetchCarrierFull(id) {
  try {
    const {
      data,
    } = await axios.get(
      `/carriers/${id}`
    );

    if (data) {
      writeCarrierCache(
        id,
        {
          carrier: data,

          files: {
            communityLicense:
              data.communityLicense ||
              null,

            cmrInsurance:
              data.cmrInsurance ||
              null,

            motorThirdPartyLiabilityInsurance:
              data.motorThirdPartyLiabilityInsurance ||
              null,

            comprehensiveVehicleInsurance:
              data.comprehensiveVehicleInsurance ||
              null,
          },
        }
      );
    }
  } catch {
    // ignore
  }
}

function mergeIndustryPrefill(
  id,
  summary
) {
  const cached =
    readIndustryCache(id) ||
    null;

  if (
    cached &&
    typeof cached === "object"
  ) {
    return {
      ...cached,
      ...(summary || {}),
    };
  }

  return summary || null;
}

function decodePayload(tok) {
  try {
    const b64 = tok
      .split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    return JSON.parse(
      atob(b64)
    );
  } catch {
    return null;
  }
}

function resolveCompanyId() {
  const tok =
    getStoredToken() ||
    localStorage.getItem(
      "authToken"
    ) ||
    "";

  const payload = tok
    ? decodePayload(tok)
    : null;

  const fromJwt =
    payload &&
    (
      payload.company_id ??
      payload.companyId
    );

  if (
    Number.isFinite(
      Number(fromJwt)
    ) &&
    Number(fromJwt) > 0
  ) {
    return Number(fromJwt);
  }

  const direct =
    sessionStorage.getItem(
      "companyId"
    ) ||
    sessionStorage.getItem(
      "company_id"
    ) ||
    localStorage.getItem(
      "companyId"
    ) ||
    localStorage.getItem(
      "company_id"
    ) ||
    null;

  if (
    Number.isFinite(
      Number(direct)
    ) &&
    Number(direct) > 0
  ) {
    return Number(direct);
  }

  const storedUser =
    getStoredUser();

  const fromUser =
    storedUser?.company_id ??
    storedUser?.companyId ??
    null;

  if (
    Number.isFinite(
      Number(fromUser)
    ) &&
    Number(fromUser) > 0
  ) {
    return Number(fromUser);
  }

  return null;
}

function clientsBaseKey(cid) {
  return cid
    ? `company:${cid}`
    : "company:unknown";
}

function clientsIndustriesCacheKey(
  cid
) {
  return `${clientsBaseKey(
    cid
  )}:industries:summary:v1`;
}

function clientsCarriersCacheKey(
  cid
) {
  return `${clientsBaseKey(
    cid
  )}:carriers:summary:v1`;
}

function readListCache(key) {
  try {
    const raw =
      window.localStorage.getItem(
        key
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    const items =
      Array.isArray(
        parsed?.items
      )
        ? parsed.items
        : Array.isArray(parsed)
        ? parsed
        : null;

    if (!items) {
      return null;
    }

    const ts =
      Number(
        parsed?.ts || 0
      );

    if (
      ts &&
      Date.now() - ts >
        CLIENTS_CACHE_TTL_MS
    ) {
      return null;
    }

    return items;
  } catch {
    return null;
  }
}

function writeListCache(
  key,
  items
) {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        ts: Date.now(),

        items:
          Array.isArray(items)
            ? items
            : [],
      })
    );
  } catch {
    // ignore
  }
}

function getCountryCode(
  country
) {
  const value =
    normalizeText(country);

  const countryCodes = {
    portugal: "pt",
    pt: "pt",

    france: "fr",
    franca: "fr",
    fr: "fr",

    spain: "es",
    espanha: "es",
    es: "es",

    germany: "de",
    alemanha: "de",
    de: "de",

    italy: "it",
    italia: "it",
    it: "it",

    belgium: "be",
    belgica: "be",
    be: "be",

    netherlands: "nl",
    holanda: "nl",
    "paises baixos": "nl",
    paisesbaixos: "nl",
    nl: "nl",

    switzerland: "ch",
    suica: "ch",
    ch: "ch",

    austria: "at",
    at: "at",

    poland: "pl",
    polonia: "pl",
    pl: "pl",
  };

  return (
    countryCodes[value] ||
    null
  );
}

const INDUSTRY_META = {
  agriculture: {
    key: "AGRICULTURE",
    tone: "clients-type-badge--agriculture",
    icon: LuWheat,
  },

  automotive: {
    key: "AUTOMOTIVE",
    tone: "clients-type-badge--automotive",
    icon: LuCar,
  },

  construction: {
    key: "CONSTRUCTION",
    tone: "clients-type-badge--construction",
    icon: LuHardHat,
  },

  education: {
    key: "EDUCATION",
    tone: "clients-type-badge--education",
    icon: LuGraduationCap,
  },

  energy: {
    key: "ENERGY",
    tone: "clients-type-badge--energy",
    icon: LuZap,
  },

  "financial services": {
    key: "FINANCIAL_SERVICES",
    tone: "clients-type-badge--financial",
    icon: LuLandmark,
  },

  "food & beverage": {
    key: "FOOD_BEVERAGE",
    tone: "clients-type-badge--food",
    icon: LuUtensilsCrossed,
  },

  healthcare: {
    key: "HEALTHCARE",
    tone: "clients-type-badge--healthcare",
    icon: LuHeartPulse,
  },

  hospitality: {
    key: "HOSPITALITY",
    tone: "clients-type-badge--hospitality",
    icon: LuHotel,
  },

  logistics: {
    key: "LOGISTICS",
    tone: "clients-type-badge--logistics",
    icon: LuTruck,
  },

  manufacturing: {
    key: "MANUFACTURING",
    tone: "clients-type-badge--manufacturing",
    icon: LuFactory,
  },

  "media & entertainment": {
    key: "MEDIA_ENTERTAINMENT",
    tone: "clients-type-badge--media",
    icon: LuClapperboard,
  },

  mining: {
    key: "MINING",
    tone: "clients-type-badge--mining",
    icon: LuPickaxe,
  },

  pharmaceuticals: {
    key: "PHARMACEUTICALS",
    tone: "clients-type-badge--pharmaceuticals",
    icon: LuPill,
  },

  "real estate": {
    key: "REAL_ESTATE",
    tone: "clients-type-badge--real-estate",
    icon: LuHouse,
  },

  retail: {
    key: "RETAIL",
    tone: "clients-type-badge--retail",
    icon: LuShoppingBag,
  },

  technology: {
    key: "TECHNOLOGY",
    tone: "clients-type-badge--technology",
    icon: LuCpu,
  },

  utilities: {
    key: "UTILITIES",
    tone: "clients-type-badge--utilities",
    icon: LuDroplets,
  },

  other: {
    key: "OTHER",
    tone: "clients-type-badge--other",
    icon: LuEllipsis,
  },
};

const getIndustryMeta = (industryType) => {
  const value =
    normalizeText(
      industryType
    );

  return (
    INDUSTRY_META[value] ||
    INDUSTRY_META.other
  );
};

const getIndustryTone = (industryType) => {
  return getIndustryMeta(
    industryType
  ).tone;
};

const getIndustryIcon = (industryType) => {
  return getIndustryMeta(
    industryType
  ).icon;
};

const getIndustryTypeLabel = (
  industryType,
  t
) => {
  if (!industryType) {
    return "";
  }

  const meta =
    getIndustryMeta(
      industryType
    );

  return t(
    `industry_types.${meta.key}`,
    {
      defaultValue:
        industryType,
    }
  );
};

const TRUCK_TYPE_META = {
  "curtain side truck (tautliner)": {
    key: "tautliner",
    tone: "clients-truck-badge--tautliner",
  },

  "refrigerated truck (frigo)": {
    key: "frigo",
    tone: "clients-truck-badge--frigo",
  },

  "articulated dump truck": {
    key: "articulated_dump",
    tone: "clients-truck-badge--articulated-dump",
  },

  "box truck / van": {
    key: "box_truck",
    tone: "clients-truck-badge--box-truck",
  },

  "car hauler": {
    key: "car_hauler",
    tone: "clients-truck-badge--car-hauler",
  },

  "cattle carrier": {
    key: "cattle",
    tone: "clients-truck-badge--cattle",
  },

  "cement mixer truck": {
    key: "cement_mixer",
    tone: "clients-truck-badge--cement-mixer",
  },

  "clothing hanging carrier": {
    key: "clothing",
    tone: "clients-truck-badge--clothing",
  },

  "concrete pump truck": {
    key: "concrete_pump",
    tone: "clients-truck-badge--concrete-pump",
  },

  "container truck": {
    key: "container",
    tone: "clients-truck-badge--container",
  },

  "crane truck": {
    key: "crane",
    tone: "clients-truck-badge--crane",
  },

  "double decker truck": {
    key: "double_decker",
    tone: "clients-truck-badge--double-decker",
  },

  "drum carrier": {
    key: "drum",
    tone: "clients-truck-badge--drum",
  },

  "dump truck (dump carrier)": {
    key: "dump",
    tone: "clients-truck-badge--dump",
  },

  "flatbed truck": {
    key: "flatbed",
    tone: "clients-truck-badge--flatbed",
  },

  "heavy hauler": {
    key: "heavy_hauler",
    tone: "clients-truck-badge--heavy-hauler",
  },

  "logging truck": {
    key: "logging",
    tone: "clients-truck-badge--logging",
  },

  "lowboy truck (special transport)": {
    key: "lowboy",
    tone: "clients-truck-badge--lowboy",
  },

  "open box truck": {
    key: "open_box",
    tone: "clients-truck-badge--open-box",
  },

  "pickup truck": {
    key: "pickup",
    tone: "clients-truck-badge--pickup",
  },

  "road train": {
    key: "road_train",
    tone: "clients-truck-badge--road-train",
  },

  "tanker truck (cistern)": {
    key: "tanker",
    tone: "clients-truck-badge--tanker",
  },

  "vehicle carrier": {
    key: "vehicle_carrier",
    tone: "clients-truck-badge--vehicle-carrier",
  },
};

const getTruckTypeValues = (truckTypes) => {
  if (Array.isArray(truckTypes)) {
    return truckTypes
      .map((value) =>
        String(value || "").trim()
      )
      .filter(Boolean);
  }

  const raw =
    String(
      truckTypes || ""
    ).trim();

  if (!raw) {
    return [];
  }

  if (
    raw.startsWith("[") &&
    raw.endsWith("]")
  ) {
    try {
      const parsed =
        JSON.parse(raw);

      if (
        Array.isArray(parsed)
      ) {
        return parsed
          .map((value) =>
            String(
              value || ""
            ).trim()
          )
          .filter(Boolean);
      }
    } catch {
      // continua para o fallback
    }
  }

  return raw
    .split(",")
    .map((value) =>
      value.trim()
    )
    .filter(Boolean);
};

const getTruckTypeMeta = (
  truckType
) => {
  const value =
    normalizeText(
      truckType
    );

  return (
    TRUCK_TYPE_META[value] ||
    null
  );
};

const getTruckTypeLabel = (
  truckType,
  t
) => {
  if (!truckType) {
    return "";
  }

  const meta =
    getTruckTypeMeta(
      truckType
    );

  if (!meta) {
    return truckType;
  }

  return t(
    `create_carrier:options.truck_types.${meta.key}`,
    {
      defaultValue:
        truckType,
    }
  );
};

const getTruckTypeTone = (
  truckType
) => {
  return (
    getTruckTypeMeta(
      truckType
    )?.tone ||
    "clients-truck-badge--default"
  );
};

/* =========================================================
   react-select styles
   ========================================================= */

const clientsFilterSelectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),

  control: (
    base,
    state
  ) => ({
    ...base,

    minHeight: 38,
    height: 38,

    background:
      "#0f1318",

    border:
      state.isFocused
        ? "1px solid #4f5dff"
        : "1px solid #263244",

    borderRadius: 10,

    boxShadow:
      state.isFocused
        ? "0 0 0 2px rgba(79,93,255,0.15)"
        : "none",

    cursor: "pointer",

    "&:hover": {
      borderColor:
        state.isFocused
          ? "#4f5dff"
          : "#34445c",
    },
  }),

  valueContainer: (
    base
  ) => ({
    ...base,
    padding:
      "0 8px 0 34px",
  }),

  singleValue: (
    base
  ) => ({
    ...base,
    color:
      "#e5e7eb",
    fontSize: 12.5,
  }),

  placeholder: (
    base
  ) => ({
    ...base,
    color:
      "#8a96a8",
    fontSize: 12.5,
  }),

  input: (base) => ({
    ...base,
    color:
      "#e5e7eb",
    margin: 0,
    padding: 0,
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...base,
    height: 38,
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (
    base
  ) => ({
    ...base,
    color:
      "#8c98aa",
    padding: 7,

    "&:hover": {
      color:
        "#ffffff",
    },
  }),

  clearIndicator: (
    base
  ) => ({
    ...base,
    color:
      "#8c98aa",
    padding: 6,

    "&:hover": {
      color:
        "#ffffff",
    },
  }),

  menuPortal: (
    base
  ) => ({
    ...base,
    zIndex: 99999,
  }),

  menu: (base) => ({
    ...base,

    marginTop: 6,

    background:
      "#111820",

    border:
      "1px solid #273548",

    borderRadius: 11,

    overflow:
      "hidden",

    boxShadow:
      "0 16px 38px rgba(0,0,0,0.48)",
  }),

  menuList: (
    base
  ) => ({
    ...base,
    padding: 5,
    maxHeight: 240,
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius: 7,

    padding:
      "8px 10px",

    fontSize: 12.5,

    cursor: "pointer",

    color:
      state.isSelected
        ? "#ffffff"
        : "#d8e0eb",

    backgroundColor:
      state.isSelected
        ? "rgba(73,83,255,0.24)"
        : state.isFocused
        ? "rgba(73,83,255,0.12)"
        : "transparent",

    "&:active": {
      backgroundColor:
        "rgba(73,83,255,0.2)",
    },
  }),

  noOptionsMessage: (
    base
  ) => ({
    ...base,
    color:
      "#8d99aa",
    fontSize: 12.5,
  }),
};

/* =========================================================
   Component
   ========================================================= */

function Clients() {
  const {
    t,
  } = useTranslation(
    [
      "clients",
      "create_carrier",
    ]
  );

  const navigate =
    useNavigate();

  const location =
    useLocation();

  useEffect(() => {
    const content =
      document.querySelector(
        ".content"
      );

    if (!content) {
      return undefined;
    }

    content.classList.add(
      "content--clients"
    );

    return () => {
      content.classList.remove(
        "content--clients"
      );
    };
  }, []);

  const [
    companyId,
    setCompanyId,
  ] = useState(
    () =>
      resolveCompanyId()
  );

  const getInitialView =
    () => {
      const params =
        new URLSearchParams(
          location.search
        );

      const v =
        params.get(
          "view"
        );

      if (
        v ===
          "carriers" ||
        v ===
          "industries"
      ) {
        return v;
      }

      return "industries";
    };

  const [
    view,
    setView,
  ] = useState(
    getInitialView()
  );

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("");

  const [
    countryFilter,
    setCountryFilter,
  ] = useState("");

  const [
    visibleLimit,
    setVisibleLimit,
  ] = useState(
    PAGE_SIZE
  );

  const sentinelRef =
    useRef(null);

  const didPrefetchRef =
    useRef(null);

  useEffect(() => {
    if (companyId) {
      return;
    }

    const id =
      resolveCompanyId();

    if (id) {
      setCompanyId(id);
    }
  }, [companyId]);

  const industriesCacheKey =
    useMemo(
      () =>
        clientsIndustriesCacheKey(
          companyId
        ),
      [companyId]
    );

  const carriersCacheKey =
    useMemo(
      () =>
        clientsCarriersCacheKey(
          companyId
        ),
      [companyId]
    );

  const [
    carriers,
    setCarriers,
  ] = useState(() => {
    const cid =
      resolveCompanyId();

    const key =
      clientsCarriersCacheKey(
        cid
      );

    return (
      readListCache(key) ||
      []
    );
  });

  const [
    industries,
    setIndustries,
  ] = useState(() => {
    const cid =
      resolveCompanyId();

    const key =
      clientsIndustriesCacheKey(
        cid
      );

    return (
      readListCache(key) ||
      []
    );
  });

  const [
    loadingIndustries,
    setLoadingIndustries,
  ] = useState(
    () =>
      industries.length ===
      0
  );

  const [
    loadingCarriers,
    setLoadingCarriers,
  ] = useState(
    () =>
      carriers.length ===
      0
  );

  /* =========================================================
     Hydrate cache
     ========================================================= */

  useEffect(() => {
    const cachedInd =
      readListCache(
        industriesCacheKey
      );

    const cachedCar =
      readListCache(
        carriersCacheKey
      );

    if (cachedInd) {
      setIndustries(
        cachedInd
      );

      setLoadingIndustries(
        false
      );
    } else {
      setLoadingIndustries(
        true
      );
    }

    if (cachedCar) {
      setCarriers(
        cachedCar
      );

      setLoadingCarriers(
        false
      );
    } else {
      setLoadingCarriers(
        true
      );
    }
  }, [
    industriesCacheKey,
    carriersCacheKey,
  ]);

  /* =========================================================
     Fetch
     ========================================================= */

  useEffect(() => {
    const guardKey =
      `prefetch:${view}:` +
      `${industriesCacheKey}|` +
      `${carriersCacheKey}`;

    if (
      didPrefetchRef.current ===
      guardKey
    ) {
      return;
    }

    didPrefetchRef.current =
      guardKey;

    const fetchIndustries =
      async () => {
        try {
          const {
            data,
          } =
            await axios.get(
              "/industries/summary"
            );

          const arr =
            Array.isArray(data)
              ? data
              : [];

          setIndustries(
            arr
          );

          writeListCache(
            industriesCacheKey,
            arr
          );

          setErrorMessage(
            ""
          );
        } catch (e) {
          console.error(
            "Error fetching industries:",
            e
          );

          setErrorMessage(
            t(
              "messages.fetch_error_industries"
            )
          );
        } finally {
          setLoadingIndustries(
            false
          );
        }
      };

    const fetchCarriers =
      async () => {
        try {
          const {
            data,
          } =
            await axios.get(
              "/carriers/summary"
            );

          const arr =
            Array.isArray(data)
              ? data
              : [];

          setCarriers(
            arr
          );

          writeListCache(
            carriersCacheKey,
            arr
          );

          setErrorMessage(
            ""
          );
        } catch (e) {
          console.error(
            "Error fetching carriers:",
            e
          );

          setErrorMessage(
            t(
              "messages.fetch_error_carriers"
            )
          );
        } finally {
          setLoadingCarriers(
            false
          );
        }
      };

    const idle =
      window.requestIdleCallback ||
      (
        (fn) =>
          setTimeout(
            fn,
            250
          )
      );

    if (
      view ===
      "industries"
    ) {
      fetchIndustries();

      idle(() => {
        const cached =
          readListCache(
            carriersCacheKey
          );

        if (
          !Array.isArray(
            cached
          ) ||
          cached.length === 0
        ) {
          fetchCarriers();
        }
      });
    } else {
      fetchCarriers();

      idle(() => {
        const cached =
          readListCache(
            industriesCacheKey
          );

        if (
          !Array.isArray(
            cached
          ) ||
          cached.length === 0
        ) {
          fetchIndustries();
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    industriesCacheKey,
    carriersCacheKey,
    view,
  ]);

  /* =========================================================
     URL view
     ========================================================= */

  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const v =
      params.get(
        "view"
      );

    if (
      v ===
        "carriers" ||
      v ===
        "industries"
    ) {
      setView(v);
    } else {
      setView(
        "industries"
      );

      navigate(
        "/clients?view=industries",
        {
          replace: true,
        }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* =========================================================
     Reset filters when changing tab
     ========================================================= */

  useEffect(() => {
    setTypeFilter("");
    setCountryFilter("");
    setVisibleLimit(
      PAGE_SIZE
    );
  }, [view]);

  /* =========================================================
     Websocket live updates
     ========================================================= */

  useEffect(() => {
    const socket =
      getSocket();

    if (!socket) {
      return;
    }

    const onIndCreated =
      ({ item }) => {
        setIndustries(
          (prev) => {
            const next =
              !Array.isArray(
                prev
              )
                ? [item]
                : prev.some(
                    (x) =>
                      x.id ===
                      item.id
                  )
                ? prev
                : [
                    item,
                    ...prev,
                  ];

            writeListCache(
              industriesCacheKey,
              next
            );

            return next;
          }
        );
      };

    const onIndUpdated =
      ({ item }) => {
        setIndustries(
          (prev) => {
            const next =
              Array.isArray(
                prev
              )
                ? prev.map(
                    (x) =>
                      x.id ===
                      item.id
                        ? {
                            ...x,
                            ...item,
                          }
                        : x
                  )
                : [item];

            writeListCache(
              industriesCacheKey,
              next
            );

            return next;
          }
        );
      };

    const onIndDeleted =
      ({ id }) => {
        setIndustries(
          (prev) => {
            const next =
              Array.isArray(
                prev
              )
                ? prev.filter(
                    (x) =>
                      x.id !==
                      id
                  )
                : [];

            writeListCache(
              industriesCacheKey,
              next
            );

            return next;
          }
        );
      };

    const onCarCreated =
      ({ item }) => {
        setCarriers(
          (prev) => {
            const next =
              !Array.isArray(
                prev
              )
                ? [item]
                : prev.some(
                    (x) =>
                      x.id ===
                      item.id
                  )
                ? prev
                : [
                    item,
                    ...prev,
                  ];

            writeListCache(
              carriersCacheKey,
              next
            );

            return next;
          }
        );
      };

    const onCarUpdated =
      ({ item }) => {
        setCarriers(
          (prev) => {
            const next =
              Array.isArray(
                prev
              )
                ? prev.map(
                    (x) =>
                      x.id ===
                      item.id
                        ? {
                            ...x,
                            ...item,
                          }
                        : x
                  )
                : [item];

            writeListCache(
              carriersCacheKey,
              next
            );

            return next;
          }
        );
      };

    const onCarDeleted =
      ({ id }) => {
        setCarriers(
          (prev) => {
            const next =
              Array.isArray(
                prev
              )
                ? prev.filter(
                    (x) =>
                      x.id !==
                      id
                  )
                : [];

            writeListCache(
              carriersCacheKey,
              next
            );

            return next;
          }
        );
      };

    socket.on(
      "industries:created",
      onIndCreated
    );

    socket.on(
      "industries:updated",
      onIndUpdated
    );

    socket.on(
      "industries:deleted",
      onIndDeleted
    );

    socket.on(
      "carriers:created",
      onCarCreated
    );

    socket.on(
      "carriers:updated",
      onCarUpdated
    );

    socket.on(
      "carriers:deleted",
      onCarDeleted
    );

    return () => {
      socket.off(
        "industries:created",
        onIndCreated
      );

      socket.off(
        "industries:updated",
        onIndUpdated
      );

      socket.off(
        "industries:deleted",
        onIndDeleted
      );

      socket.off(
        "carriers:created",
        onCarCreated
      );

      socket.off(
        "carriers:updated",
        onCarUpdated
      );

      socket.off(
        "carriers:deleted",
        onCarDeleted
      );
    };
  }, [
    industriesCacheKey,
    carriersCacheKey,
  ]);

  /* =========================================================
     Current list
     ========================================================= */

  const list =
    view === "carriers"
      ? carriers
      : industries;

  const isLoadingCurrent =
    view === "carriers"
      ? loadingCarriers
      : loadingIndustries;

  /* =========================================================
     Filter options
     ========================================================= */

  const typeOptions = useMemo(() => {
    const values =
      view === "carriers"
        ? list.flatMap(
            (item) =>
              getTruckTypeValues(
                item.truckTypes
              )
          )
        : list
            .map((item) =>
              stringifyValue(
                item.industryType
              )
            )
            .map((value) =>
              value.trim()
            )
            .filter(Boolean);

    return Array.from(
      new Set(values)
    )
      .map((value) => ({
        value,

        label:
          view === "industries"
            ? getIndustryTypeLabel(
                value,
                t
              )
            : getTruckTypeLabel(
                value,
                t
              ),
      }))
      .sort((a, b) =>
        a.label.localeCompare(
          b.label,
          undefined,
          {
            sensitivity:
              "base",
          }
        )
      );
  }, [
    list,
    view,
    t,
  ]);

  const countryOptions =
    useMemo(() => {
      const values =
        list
          .map(
            (item) =>
              item.country
          )
          .filter(Boolean)
          .map((value) =>
            String(value).trim()
          )
          .filter(Boolean);

      return Array.from(
        new Set(values)
      )
        .sort(
          (a, b) =>
            a.localeCompare(
              b,
              "pt",
              {
                sensitivity:
                  "base",
              }
            )
        )
        .map((value) => ({
          value,
          label: value,
        }));
    }, [list]);

  const selectedTypeOption =
    typeOptions.find(
      (option) =>
        option.value ===
        typeFilter
    ) || null;

  const selectedCountryOption =
    countryOptions.find(
      (option) =>
        option.value ===
        countryFilter
    ) || null;

  const menuPortalTarget =
    typeof document !==
    "undefined"
      ? document.body
      : null;

  /* =========================================================
     Filter list
     ========================================================= */

  const filteredClients =
    useMemo(() => {
      const query =
        normalizeText(
          searchTerm
        );

      return list.filter(
        (item) => {
          const industryTypeValue =
            view === "industries"
              ? stringifyValue(
                  item.industryType
                )
              : "";

          const truckTypeValues =
            view === "carriers"
              ? getTruckTypeValues(
                  item.truckTypes
                )
              : [];

          const translatedIndustryType =
            view === "industries"
              ? getIndustryTypeLabel(
                  industryTypeValue,
                  t
                )
              : "";

          const translatedTruckTypes =
            truckTypeValues.map(
              (truckType) =>
                getTruckTypeLabel(
                  truckType,
                  t
                )
            );

          const searchText = [
            item.companyName,

            industryTypeValue,
            translatedIndustryType,

            ...truckTypeValues,
            ...translatedTruckTypes,

            item.phoneNumber,
            item.city,
            item.country,
          ]
            .filter(Boolean)
            .join(" ");

          const matchesSearch =
            !query ||
            normalizeText(
              searchText
            ).includes(
              query
            );

          const matchesType =
            !typeFilter ||
            (
              view === "carriers"
                ? truckTypeValues.includes(
                    typeFilter
                  )
                : industryTypeValue ===
                  typeFilter
            );

          const matchesCountry =
            !countryFilter ||
            item.country ===
              countryFilter;

          return (
            matchesSearch &&
            matchesType &&
            matchesCountry
          );
        }
      );
    }, [
      list,
      searchTerm,
      typeFilter,
      countryFilter,
      view,
      t,
    ]);

  const totalCount =
    list.length;

  const visibleCount =
    filteredClients.length;

  const isSearching =
    searchTerm.trim()
      .length > 0;

  const hasActiveFilters =
    Boolean(
      searchTerm.trim() ||
        typeFilter ||
        countryFilter
    );

  const renderedCount =
    isSearching
      ? visibleCount
      : Math.min(
          visibleLimit,
          visibleCount
        );

  const visibleClients =
    useMemo(() => {
      if (isSearching) {
        return filteredClients;
      }

      return filteredClients.slice(
        0,
        visibleLimit
      );
    }, [
      filteredClients,
      visibleLimit,
      isSearching,
    ]);

  useEffect(() => {
    if (!isSearching) {
      setVisibleLimit(
        PAGE_SIZE
      );
    }
  }, [
    searchTerm,
    typeFilter,
    countryFilter,
    view,
    isSearching,
  ]);

  /* =========================================================
     Infinite scroll
     ========================================================= */

  useEffect(() => {
    if (isSearching) {
      return;
    }

    if (
      !sentinelRef.current
    ) {
      return;
    }

    if (
      visibleLimit >=
      visibleCount
    ) {
      return;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          if (
            entries.some(
              (entry) =>
                entry.isIntersecting
            )
          ) {
            setVisibleLimit(
              (value) =>
                Math.min(
                  value +
                    PAGE_SIZE,
                  visibleCount
                )
            );
          }
        },
        {
          root: null,
          rootMargin:
            "220px 0px",
          threshold: 0.01,
        }
      );

    observer.observe(
      sentinelRef.current
    );

    return () =>
      observer.disconnect();
  }, [
    isSearching,
    visibleLimit,
    visibleCount,
  ]);

  /* =========================================================
     Actions
     ========================================================= */

  const handleToggle = (
    selectedView
  ) => {
    if (
      selectedView !==
      view
    ) {
      navigate(
        `/clients?view=${selectedView}`
      );
    }
  };

  const handleRemove =
    async (id) => {
      const ok =
        window.confirm(
          view ===
            "industries"
            ? t(
                "confirm.remove_industry"
              )
            : t(
                "confirm.remove_carrier"
              )
        );

      if (!ok) {
        return;
      }

      try {
        const endpoint =
          view ===
          "carriers"
            ? `/carriers/${id}`
            : `/industries/${id}`;

        await axios.delete(
          endpoint
        );

        if (
          view ===
          "carriers"
        ) {
          setCarriers(
            (prev) => {
              const next =
                prev.filter(
                  (x) =>
                    x.id !==
                    id
                );

              writeListCache(
                carriersCacheKey,
                next
              );

              return next;
            }
          );
        } else {
          setIndustries(
            (prev) => {
              const next =
                prev.filter(
                  (x) =>
                    x.id !==
                    id
                );

              writeListCache(
                industriesCacheKey,
                next
              );

              return next;
            }
          );
        }
      } catch {
        setErrorMessage(
          view ===
            "carriers"
            ? t(
                "messages.remove_error_carrier"
              )
            : t(
                "messages.remove_error_industry"
              )
        );
      }
    };

  const handleCreate = () => {
    if (
      view ===
      "carriers"
    ) {
      navigate(
        "/clients/create/carrier"
      );

      return;
    }

    navigate(
      "/clients/create/industry"
    );
  };

  const handleEdit =
    async (id) => {
      if (
        view ===
        "carriers"
      ) {
        const summary =
          carriers.find(
            (x) =>
              String(x.id) ===
              String(id)
          ) || null;

        const cached =
          readCarrierCache(
            id
          ) || null;

        const cachedCarrier =
          cached?.carrier ||
          null;

        let fullCarrier =
          cachedCarrier;

        if (!fullCarrier) {
          try {
            const {
              data,
            } =
              await axios.get(
                `/carriers/${id}`
              );

            fullCarrier =
              data || null;
          } catch {
            fullCarrier =
              null;
          }
        }

        const prefill =
          fullCarrier
            ? {
                ...fullCarrier,
                ...(summary ||
                  {}),
              }
            : summary ||
              null;

        if (prefill) {
          writeCarrierCache(
            id,
            {
              carrier:
                prefill,

              files: {
                communityLicense:
                  prefill.communityLicense ||
                  null,

                cmrInsurance:
                  prefill.cmrInsurance ||
                  null,

                motorThirdPartyLiabilityInsurance:
                  prefill.motorThirdPartyLiabilityInsurance ||
                  null,

                comprehensiveVehicleInsurance:
                  prefill.comprehensiveVehicleInsurance ||
                  null,
              },
            }
          );
        }

        navigate(
          `/clients/edit/carrier/${id}`,
          {
            state: {
              prefill,
            },
          }
        );

        return;
      }

      const summary =
        industries.find(
          (x) =>
            String(x.id) ===
            String(id)
        ) || null;

      const mergedPrefill =
        mergeIndustryPrefill(
          id,
          summary
        );

      if (
        mergedPrefill
      ) {
        writeIndustryCache(
          id,
          mergedPrefill
        );

        prefetchIndustryFull(
          id
        );
      } else {
        prefetchIndustryFull(
          id
        );
      }

      navigate(
        `/clients/edit/industry/${id}`,
        {
          state: {
            prefill:
              mergedPrefill,
          },
        }
      );
    };

  const tableModeClass =
    view === "carriers"
      ? "is-carriers"
      : "is-industries";

  /* =========================================================
     Render
     ========================================================= */

  return (
    <div className="clients-page">
      {/* ===========================
          Header
          =========================== */}
      <section className="clients-card clients-toolbar">
        <div className="clients-toolbar__left">
          <div className="clients-header-copy">
            <h1 className="clients-header-title">
              {t("header.title", {
                defaultValue:
                  "Clientes",
              })}
            </h1>

            <p className="clients-header-subtitle">
              <strong>
                {totalCount}
              </strong>{" "}
              {t(
                "header.registered",
                {
                  defaultValue:
                    "clientes registados",
                }
              )}
            </p>
          </div>
        </div>

        {/* Toggle original - mantido */}
        <div className="clients-toggle-container">
          <div
            className="clients-toggle-slider"
            data-view={view}
          />

          <button
            className={`clients-toggle-button ${
              view ===
              "industries"
                ? "active"
                : ""
            }`}
            onClick={() =>
              handleToggle(
                "industries"
              )
            }
            type="button"
          >
            <LuFactory className="clients-toggle-icon" />

            <span>
              {t(
                "toggle.industries"
              )}
            </span>
          </button>

          <button
            className={`clients-toggle-button ${
              view ===
              "carriers"
                ? "active"
                : ""
            }`}
            onClick={() =>
              handleToggle(
                "carriers"
              )
            }
            type="button"
          >
            <LuTruck className="clients-toggle-icon" />

            <span>
              {t(
                "toggle.carriers"
              )}
            </span>
          </button>
        </div>

        <div className="clients-toolbar__right">
          <div className="clients-search">
            <FaSearch className="clients-search__icon" />

            <input
              className="clients-search__input"
              type="text"
              value={
                searchTerm
              }
              onChange={(
                event
              ) =>
                setSearchTerm(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "search.placeholder"
              )}
              aria-label={t(
                "search.aria"
              )}
            />

            {searchTerm && (
              <button
                type="button"
                className="clients-search__clear"
                onClick={() =>
                  setSearchTerm(
                    ""
                  )
                }
                aria-label={t(
                  "search.clear_aria"
                )}
                title={t(
                  "search.clear_title"
                )}
              >
                ✕
              </button>
            )}
          </div>

          <div className="clients-toolbar-filter clients-toolbar-filter--type">
            <FaFilter className="clients-toolbar-filter__icon" />

            <Select
              value={
                selectedTypeOption
              }
              options={
                typeOptions
              }
              onChange={(
                option
              ) =>
                setTypeFilter(
                  option?.value ||
                    ""
                )
              }
              placeholder={
                view ===
                "carriers"
                  ? t(
                      "filters.truck_types",
                      {
                        defaultValue:
                          "Tipos de camião",
                      }
                    )
                  : t(
                      "filters.industry_type",
                      {
                        defaultValue:
                          "Tipo de indústria",
                      }
                    )
              }
              styles={
                clientsFilterSelectStyles
              }
              menuPortalTarget={
                menuPortalTarget
              }
              menuPosition="fixed"
              className="clients-header-select"
              classNamePrefix="clients-header-select"
              isSearchable={
                false
              }
              isClearable
              noOptionsMessage={() =>
                t(
                  "filters.no_options",
                  {
                    defaultValue:
                      "Sem opções",
                  }
                )
              }
            />
          </div>

          <div className="clients-toolbar-filter clients-toolbar-filter--country">
            <FaGlobeEurope className="clients-toolbar-filter__icon" />

            <Select
              value={
                selectedCountryOption
              }
              options={
                countryOptions
              }
              onChange={(
                option
              ) =>
                setCountryFilter(
                  option?.value ||
                    ""
                )
              }
              placeholder={t(
                "filters.country",
                {
                  defaultValue:
                    "País",
                }
              )}
              styles={
                clientsFilterSelectStyles
              }
              menuPortalTarget={
                menuPortalTarget
              }
              menuPosition="fixed"
              className="clients-header-select"
              classNamePrefix="clients-header-select"
              isSearchable={
                false
              }
              isClearable
              noOptionsMessage={() =>
                t(
                  "filters.no_options",
                  {
                    defaultValue:
                      "Sem opções",
                  }
                )
              }
            />
          </div>

          <button
            className="clients-btn clients-btn--primary"
            onClick={
              handleCreate
            }
            type="button"
          >
            {view ===
            "carriers"
              ? t(
                  "buttons.new_carrier"
                )
              : t(
                  "buttons.new_industry"
                )}
          </button>
        </div>
      </section>

      {/* ===========================
          Error
          =========================== */}
      {errorMessage && (
        <section className="clients-card clients-error">
          <div className="clients-error__text">
            {errorMessage}
          </div>
        </section>
      )}

      {/* ===========================
          Table
          =========================== */}
      <section className="clients-card clients-table-card">
        <div
          className={`clients-table ${tableModeClass}`}
        >
          <div className="clients-table__header">
            <div>
              {t(
                "table.company_name"
              )}
            </div>

            <div>
              {view ===
              "carriers"
                ? t(
                    "table.truck_types"
                  )
                : t(
                    "table.industry_type"
                  )}
            </div>

            <div>
              {t(
                "table.phone_number"
              )}
            </div>

            <div>
              {t(
                "table.city"
              )}
            </div>

            <div>
              {t(
                "table.country"
              )}
            </div>

            <div className="clients-col-actions">
              {t(
                "table.actions"
              )}
            </div>
          </div>

          {isLoadingCurrent &&
          list.length === 0 ? (
            Array.from({
              length: 6,
            }).map(
              (
                _,
                index
              ) => (
                <div
                  className="clients-row clients-row--skeleton"
                  key={`clients-skeleton-${index}`}
                >
                  <div className="clients-cell">
                    <span className="clients-skel" />
                  </div>

                  <div className="clients-cell">
                    <span className="clients-skel" />
                  </div>

                  <div className="clients-cell">
                    <span className="clients-skel" />
                  </div>

                  <div className="clients-cell">
                    <span className="clients-skel" />
                  </div>

                  <div className="clients-cell">
                    <span className="clients-skel" />
                  </div>

                  <div className="clients-cell clients-cell--actions">
                    <span className="clients-skel clients-skel--small" />
                  </div>
                </div>
              )
            )
          ) : visibleCount >
            0 ? (
            <>
              {visibleClients.map(
                (item) => {
                  const countryCode =
                    getCountryCode(
                      item.country
                    );

                  const typeValue =
                    view ===
                    "industries"
                      ? stringifyValue(
                          item.industryType
                        )
                      : "";

                  const truckTypeValues =
                    view ===
                    "carriers"
                      ? getTruckTypeValues(
                          item.truckTypes
                        )
                      : [];

                  const displayedTypeValue =
                    view ===
                    "industries"
                      ? getIndustryTypeLabel(
                          typeValue,
                          t
                        )
                      : "";

                  const IndustryIcon =
                    view ===
                    "industries"
                      ? getIndustryIcon(
                          typeValue
                        )
                      : LuTruck;

                  const primaryTruckType =
                    truckTypeValues[0] ||
                    null;

                  const remainingTruckTypeCount =
                    Math.max(
                      truckTypeValues.length - 1,
                      0
                    );

                  return (
                    <div
                      key={
                        item.id
                      }
                      className="clients-row"
                      onClick={() =>
                        handleEdit(
                          item.id
                        )
                      }
                      onMouseEnter={() => {
                        if (
                          view ===
                          "industries"
                        ) {
                          prefetchIndustryFull(
                            item.id
                          );
                        }

                        if (
                          view ===
                          "carriers"
                        ) {
                          prefetchCarrierFull(
                            item.id
                          );
                        }
                      }}
                      onFocus={() => {
                        if (
                          view ===
                          "industries"
                        ) {
                          prefetchIndustryFull(
                            item.id
                          );
                        }

                        if (
                          view ===
                          "carriers"
                        ) {
                          prefetchCarrierFull(
                            item.id
                          );
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(
                        event
                      ) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key ===
                            " "
                        ) {
                          event.preventDefault();

                          handleEdit(
                            item.id
                          );
                        }
                      }}
                    >
                      {/* Company */}
                      <div
                        className="clients-cell clients-company-cell"
                        title={
                          item.companyName ||
                          ""
                        }
                      >
                        <span className="clients-company-icon">
                          <LuBuilding2 />
                        </span>

                        <span className="clients-company-name">
                          {item.companyName ||
                            t(
                              "na"
                            )}
                        </span>
                      </div>

                      {/* Industry / truck types */}
                      <div
                        className="clients-cell clients-type-cell"
                        title={
                          view === "industries"
                            ? displayedTypeValue || ""
                            : undefined
                        }
                      >
                        {view ===
                        "industries" ? (
                          typeValue ? (
                            <span
                              className={`clients-type-badge ${getIndustryTone(
                                typeValue
                              )}`}
                            >
                              <IndustryIcon />

                              <span>
                                {displayedTypeValue}
                              </span>
                            </span>
                          ) : (
                            t(
                              "na"
                            )
                          )
                        ) : primaryTruckType ? (
                          <div className="clients-truck-types-summary">
                            <span
                              className={`clients-type-badge clients-truck-primary-badge ${getTruckTypeTone(
                                primaryTruckType
                              )}`}
                            >
                              <LuTruck />

                              <span>
                                {getTruckTypeLabel(
                                  primaryTruckType,
                                  t
                                )}
                              </span>
                            </span>

                            {remainingTruckTypeCount > 0 && (
                              <div
                                className="clients-truck-more"
                                onClick={(
                                  event
                                ) =>
                                  event.stopPropagation()
                                }
                                onMouseDown={(
                                  event
                                ) =>
                                  event.stopPropagation()
                                }
                              >
                                <button
                                  type="button"
                                  className="clients-truck-more__button"
                                  aria-label={`${t(
                                    "table.truck_types"
                                  )}: +${remainingTruckTypeCount}`}
                                  onClick={(
                                    event
                                  ) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  +{remainingTruckTypeCount}
                                </button>

                                <div
                                  className="clients-truck-more__popover"
                                  role="tooltip"
                                >
                                  <div className="clients-truck-more__header">
                                    <LuTruck />

                                    <span>
                                      {t(
                                        "table.truck_types"
                                      )}
                                    </span>
                                  </div>

                                  <div className="clients-truck-more__list">
                                    {truckTypeValues.map(
                                      (
                                        truckType,
                                        index
                                      ) => (
                                        <span
                                          key={`${truckType}-popover-${index}`}
                                          className={`clients-type-badge ${getTruckTypeTone(
                                            truckType
                                          )}`}
                                        >
                                          <LuTruck />

                                          <span>
                                            {getTruckTypeLabel(
                                              truckType,
                                              t
                                            )}
                                          </span>
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          t(
                            "na"
                          )
                        )}
                      </div>

                      {/* Phone */}
                      <div
                        className="clients-cell clients-cell--with-icon"
                        title={
                          item.phoneNumber ||
                          ""
                        }
                      >
                        <LuPhone className="clients-cell-icon" />

                        <span>
                          {item.phoneNumber ||
                            t(
                              "na"
                            )}
                        </span>
                      </div>

                      {/* City */}
                      <div
                        className="clients-cell clients-cell--with-icon"
                        title={
                          item.city ||
                          ""
                        }
                      >
                        <LuMapPin className="clients-cell-icon" />

                        <span>
                          {item.city ||
                            t(
                              "na"
                            )}
                        </span>
                      </div>

                      {/* Country */}
                      <div
                        className="clients-cell clients-cell--with-icon"
                        title={
                          item.country ||
                          ""
                        }
                      >
                        {countryCode ? (
                          <span
                            className={`clients-country-flag fi fi-${countryCode}`}
                            aria-hidden="true"
                          />
                        ) : (
                          <span
                            className="clients-country-flag clients-country-flag--fallback"
                            aria-hidden="true"
                          />
                        )}

                        <span>
                          {item.country ||
                            t(
                              "na"
                            )}
                        </span>
                      </div>

                      {/* Actions */}
                      <div
                        className="clients-cell clients-cell--actions"
                        onClick={(
                          event
                        ) =>
                          event.stopPropagation()
                        }
                      >
                        <button
                          className="clients-icon-btn"
                          title={t(
                            "actions.remove"
                          )}
                          onClick={() =>
                            handleRemove(
                              item.id
                            )
                          }
                          aria-label={t(
                            "actions.remove"
                          )}
                          type="button"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </div>
                  );
                }
              )}

              {!isSearching &&
                renderedCount <
                  visibleCount && (
                  <div
                    ref={
                      sentinelRef
                    }
                    className="clients-infinite-sentinel"
                  />
                )}
            </>
          ) : (
            <div className="clients-empty">
              <div className="clients-empty__title">
                {view ===
                "carriers"
                  ? t(
                      "empty.no_carriers"
                    )
                  : t(
                      "empty.no_industries"
                    )}
              </div>

              <div className="clients-empty__subtitle">
                {view ===
                "carriers"
                  ? t(
                      "empty.add_first_carrier"
                    )
                  : t(
                      "empty.add_first_industry"
                    )}
              </div>

              <button
                className="clients-btn clients-btn--primary"
                onClick={
                  handleCreate
                }
                type="button"
              >
                {view ===
                "carriers"
                  ? t(
                      "buttons.add_carrier"
                    )
                  : t(
                      "buttons.add_industry"
                    )}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Clients;